import { useQuery } from "@tanstack/react-query";
import { Inbox } from "lucide-react";

import type { SubmissionPayload } from "@/api/db/connection";
import { orpc } from "@/client";
import { CadastroLabels } from "@/lib/labels";
import { djs } from "@/lib/djs";

import { SideBlock } from "./ficha-sections";

const PAYLOAD_ORDER: (keyof SubmissionPayload)[] = [
	"name",
	"cpf",
	"birth_date",
	"rg",
	"mother_name",
	"marital_status",
	"occupation",
	"phone",
	"email",
	"cep",
	"address",
	"preferred_channel",
	"how_found",
	"how_found_detail",
	"insurance",
];

function fieldLabel(key: keyof SubmissionPayload) {
	return (CadastroLabels.field as Record<string, string | undefined>)[key] ?? key;
}

function filledEntries(payload: SubmissionPayload) {
	return PAYLOAD_ORDER.flatMap((key) => {
		const value = payload[key];

		if (value === undefined || value === "") {
			return [];
		}

		return [{ key, value: String(value) }];
	});
}

export function FichaSubmissions({ personId }: { personId: number }) {
	const { data } = useQuery(orpc.people.submissions.queryOptions({ input: { id: personId } }));

	if (!data || data.length === 0) {
		return null;
	}

	const submissions = data.toReversed();

	return (
		<SideBlock icon={<Inbox className="size-4" />} title="Histórico de envios">
			<ul data-component="ficha-submissions" className="flex flex-col gap-4">
				{submissions.map((submission) => (
					<li
						key={submission.id}
						data-component="ficha-submission"
						data-id={submission.id}
						className="flex flex-col gap-2 border-l-2 border-border pl-3"
					>
						<div className="flex flex-wrap items-baseline justify-between gap-2">
							<span className="font-mono text-xs text-muted-foreground">
								{djs(submission.created_at).format("DD/MM/YYYY HH:mm")}
							</span>

							{!!submission.photo_file && (
								<span className="text-xs text-muted-foreground">com foto</span>
							)}
						</div>

						<dl className="grid grid-cols-1 gap-x-6">
							{filledEntries(submission.payload).map((entry) => (
								<div key={entry.key} className="flex flex-col gap-0.5 py-1">
									<dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
										{fieldLabel(entry.key)}
									</dt>
									<dd className="text-sm text-foreground">{entry.value}</dd>
								</div>
							))}
						</dl>
					</li>
				))}
			</ul>
		</SideBlock>
	);
}
